// BlogPostReview.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../AuthContext";
import "./BlogPostReview.css";

const BlogPostReview = () => {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/blog/posts/id/${postId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPost(response.data);
        setError(null);
      } catch (err) {
        setError("Failed to fetch post details");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [postId, token]);

  const handleApprove = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/blog/posts/${postId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      navigate("/staff/blog");
    } catch (err) {
      setError(
        `Failed to approve post: ${err.response?.data?.message || err.message}`
      );
      console.error(err);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      setError("Please provide a reason for rejection");
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/blog/posts/${postId}/reject`,
        { rejectionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      navigate("/staff/blog");
    } catch (err) {
      setError(
        `Failed to reject post: ${err.response?.data?.message || err.message}`
      );
      console.error(err);
    }
  };

  if (loading) return <div className="loading">Loading post...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!post) return <div className="error">Post not found</div>;

  return (
    <div className="blog-post-review">
      <div className="review-header">
        <h1>Review Blog Post</h1>
        <div className="post-meta">
          <span className="post-author">
            By: {post.author.firstName} {post.author.lastName}
          </span>
          <span className="post-date">
            Submitted: {new Date(post.updatedAt).toLocaleDateString()}
          </span>
        </div>
      </div>

      <div className="review-content">
        <div className="post-preview">
          <h2>{post.title}</h2>
          <div className="post-category">Category: {post.category}</div>
          <div className="post-tags">
            Tags: {post.tags.join(", ") || "None"}
          </div>
          <div className="post-excerpt">
            <h3>Excerpt:</h3>
            <p>{post.excerpt}</p>
          </div>
          <div className="post-content">
            <h3>Content:</h3>
            <div dangerouslySetInnerHTML={{ __html: post.content }} />
          </div>

          {post.isPublic && (
            <div className="public-preview">
              <h3>Public Preview:</h3>
              <p>{post.publicExcerpt || post.excerpt}</p>
            </div>
          )}
        </div>

        <div className="review-actions">
          <div className="action-buttons">
            <button className="approve-btn" onClick={handleApprove}>
              Approve Post
            </button>
            <button className="reject-btn" onClick={handleReject}>
              Reject Post
            </button>
            <button
              className="cancel-btn"
              onClick={() => navigate("/staff/blog")}
            >
              Cancel
            </button>
          </div>

          <div className="rejection-reason">
            <h3>Rejection Reason (required if rejecting):</h3>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Provide feedback to the author about why this post was rejected and what changes are needed..."
              rows={5}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPostReview;
